import {useEffect, useRef, useState} from 'react';
import {useLatest} from 'ahooks';
import type {EntityCardProps} from './entity-card.types';
import {ENTITY_CARD_REFRESH_INTERVAL_MS, ENTITY_CARD_REFRESH_TIMEOUT_MS, MAX_TIMER_DELAY_MS, MILLISECONDS_PER_SECOND} from './entity-card.types';
import {getEntityCardAction} from './actions/entity-card-action-registry';

export function useRefreshedEntityCard(initialCard: EntityCardProps): EntityCardProps {
  const [card, setCard] = useState(initialCard);
  const isRequestInFlightRef = useRef(false);
  const fetchRefreshedCard = getEntityCardAction(card.entityType)?.fetchRefreshedCard;
  const entityRef = useLatest(card.entity ?? {});
  const hasRefreshMomentCome = useHasMomentCome(card.refreshAt);
  const isRefreshing = (card.refreshable === true || hasRefreshMomentCome) && card.entity !== undefined;

  useEffect((): (() => void) | undefined => {
    if (!isRefreshing || fetchRefreshedCard === undefined) {
      return undefined;
    }

    const startedAt = Date.now();
    const refreshCard = (): void => {
      if (isRequestInFlightRef.current) {
        return;
      }

      isRequestInFlightRef.current = true;
      void fetchRefreshedCard(entityRef.current)
        .then((refreshedCard): void => {
          if (refreshedCard === null) {
            return;
          }

          setCard(refreshedCard);
        })
        .catch((error: unknown): void => {
          console.error(error);
        })
        .finally((): void => {
          isRequestInFlightRef.current = false;
        });
    };

    refreshCard();
    const refreshTimer = window.setInterval((): void => {
      if (Date.now() - startedAt > ENTITY_CARD_REFRESH_TIMEOUT_MS) {
        window.clearInterval(refreshTimer);
        return;
      }

      refreshCard();
    }, ENTITY_CARD_REFRESH_INTERVAL_MS);

    return (): void => {
      window.clearInterval(refreshTimer);
    };
  }, [isRefreshing, fetchRefreshedCard, entityRef]);

  return card;
}

/**
 * Whether the moment (Unix seconds) has come, turning true when it does while the card is on screen; a card whose
 * moment changes, or goes, waits for its own.
 */
function useHasMomentCome(momentSeconds: number | undefined): boolean {
  const [cameMoment, setCameMoment] = useState<number | undefined>(() => (hasMomentPassed(momentSeconds) ? momentSeconds : undefined));

  useEffect((): (() => void) | undefined => {
    if (momentSeconds === undefined || cameMoment === momentSeconds) {
      return undefined;
    }

    const delay = momentSeconds * MILLISECONDS_PER_SECOND - Date.now();
    if (delay > MAX_TIMER_DELAY_MS) {
      return undefined;
    }

    const timer = window.setTimeout((): void => {
      setCameMoment(momentSeconds);
    }, Math.max(delay, 0));

    return (): void => {
      window.clearTimeout(timer);
    };
  }, [momentSeconds, cameMoment]);

  return momentSeconds !== undefined && cameMoment === momentSeconds;
}

function hasMomentPassed(momentSeconds: number | undefined): boolean {
  return momentSeconds !== undefined && momentSeconds * MILLISECONDS_PER_SECOND <= Date.now();
}
